<script setup lang="ts">
// AccountSwitcher (contract 07 §10, ORP-18636) — replaces v3 `components/App/AccountSelect.vue`
// (93 ln). v3 reached `accountStore`/`workspaceStore`/`layoutStore` + `router` directly (`:43-45`),
// hardcoded its copy (`:4` 'Search Account', `:31` a ternary pluraliser), called
// `layoutStore.activeLoader()` and did a `router.replace` for a DASHBOARDS concern (`:71-76` — now
// 09's, via `registerNavReset`). NONE of that is here: this component has ZERO store / layoutStore
// reach, and its one router call is the landing described below — not v3's. It reads
// `useAccount()`, drives the switch through `useAccount().selectAccount` (→ `SelectAccountFlow`),
// renders on `shared/ui` primitives, and emits `select` for its host (05).
//
// That one router reach arrived with ORP-21835 item 3: the flow now returns a `landing` for a
// switch that must not stay on the current screen (an MCP-born account, which owns none of the
// web surface), and this component performs it. That is the repo's existing split
// for the very same decision — `AuthCheckPermissionFlow` resolves the invite funnel's MCP landing
// and `useTeamJoin` navigates to it. The DASHBOARDS `router.replace` v3 did here is still gone.
//
// ORP-21835 items 1-2 add the MCP-born MARK. It is deliberately a second, earlier answer to the
// same question the landing above asks: `AccountItem.is_mcp_only` rides the account LIST, so a row
// can be marked BEFORE the user commits to it, while `AccountDetail.is_mcp_only` only exists after
// that account has booted. Neither is re-derived from `package` (ORP-21656 AC 2 forbids it) and
// both read absent-as-web, so a warm response cache or an older backend costs a mark, never a lie.
//
// The mark rides `SelectOption.meta`, which is where this component already puts its row data, so
// it is available to any slot that renders a row; the compact option template below is the one the
// product mounts (`AccountArea`), and is where it is drawn.
//
// What survives from v3 is the selected-first ordering (`:64`). SEARCH is now the `Select`
// primitive's own `searchable` behaviour, not a hand-rolled filter, and the workspace-count
// pluraliser is a real plural message instead of `count > 1 ? 'Workspaces' : 'Workspace'`.
import { computed, ref } from 'vue'
import { useRouter, type RouteLocationRaw } from 'vue-router'

import { useAccount } from '../composables/useAccount'
import { useAccountSession } from '../composables/useAccountSession'
import type { AccountItem } from '../models/account.types'

import { openMcpShellLogin } from '@/features/auth'
import { useI18n } from '@/shared/i18n'
import { MCP_SHELL_ORIGIN } from '@/shared/lib/mcpShell'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Modal } from '@/shared/ui/modal'
import { useToast } from '@/shared/ui/feedback'
import { Icon } from '@/shared/ui/icon'
import { Select, type SelectOption } from '@/shared/ui/select'
import { Skeleton } from '@/shared/ui/skeleton'
import type { UiAsyncState } from '@/shared/ui/types'

const props = withDefaults(defineProps<{ compact?: boolean; disabled?: boolean }>(), {
  compact: false,
  disabled: false,
})
const emit = defineEmits<{
  select: [account: AccountItem]
  switching: [value: boolean]
}>()

const { t } = useI18n()
const router = useRouter()
const toast = useToast()
const { accounts, account, accountId, selectAccount } = useAccount()
const { isBooting } = useAccountSession()

/** Local pending state — a switch keeps the shell mounted and never re-enters the boot splash
 *  (contract §18: "switches use local flow pending state"). */
const switching = ref(false)

const isCurrent = (account: AccountItem): boolean => Number(accountId.value) === Number(account.id)

/** loading → skeleton · empty (nothing to switch between) → hidden · ready → the Select.
 *  `switching` is not a `UiAsyncState`; it disables the control instead (contract §10). */
const state = computed<UiAsyncState>(() => {
  if (isBooting.value) return 'loading'
  return accounts.value.length <= 1 ? 'empty' : 'ready'
})

// Selected-first, then each row labelled with its workspace count (v3 `:29-32`, pluralised).
const options = computed<SelectOption<number>[]>(() =>
  [...accounts.value]
    .sort((a, b) => Number(isCurrent(b)) - Number(isCurrent(a)))
    .map((account) => ({
      value: Number(account.id),
      label: `${account.name ?? ''} · ${t('account.switcher.workspaceCount', Number(account.workspace_count) || 0)}`,
      meta: {
        name: String(account.name ?? account.id),
        workspaceCount: Number(account.workspace_count) || 0,
        // `=== true` for the same reason the router and the flow use it: an unmarked row must
        // read as a web account, never as an unknown, so a missing field costs only the badge.
        isMcpOnly: account.is_mcp_only === true,
      },
    })),
)

function optionName(option: SelectOption<number>): string {
  return String(option.meta?.name ?? option.label)
}

function optionWorkspaceCount(option: SelectOption<number>): number {
  return Number(option.meta?.workspaceCount) || 0
}

function optionIsMcpOnly(option: SelectOption<number>): boolean {
  return option.meta?.isMcpOnly === true
}

// An MCP-only account lives in the Orphex MCP app (v3 `AccountSelect.vue`, 905ca4283): picking one
// asks first, then opens mcp.orphex.co on that account instead of switching this app into it.
const mcpConfirmAccount = ref<AccountItem | null>(null)
const mcpConfirmOpen = computed({
  get: () => mcpConfirmAccount.value !== null,
  set: (open: boolean) => {
    if (!open) mcpConfirmAccount.value = null
  },
})
const mcpShellHost = new URL(MCP_SHELL_ORIGIN).host

function goToMcpShell(): void {
  const target = mcpConfirmAccount.value
  mcpConfirmAccount.value = null
  // Called inside the click, so the popup blocker lets the tab through.
  if (target) void openMcpShellLogin(target.id)
}

async function onChange(value: number | null): Promise<void> {
  const account = accounts.value.find((item) => Number(item.id) === value)
  if (!account) return
  if (account.is_mcp_only === true && !isCurrent(account)) {
    mcpConfirmAccount.value = account
    return
  }
  switching.value = true
  emit('switching', true)
  emit('select', account)
  let landing: RouteLocationRaw | undefined
  try {
    // Re-selecting the CURRENT account is a no-op inside the flow — never a re-boot (analysis R9).
    const outcome = await selectAccount(account)
    if (outcome.status === 'failed') {
      toast.error(t('account.switch.failed'))
    } else if (outcome.status === 'switched' && outcome.landing) {
      landing = outcome.landing
    }
  } finally {
    switching.value = false
    emit('switching', false)
  }

  if (landing) {
    // The targeted landing (ORP-21835 item 3). The flow decided WHETHER and WHERE — it is the
    // only place the freshly-booted `is_mcp_only` exists — and this component owns the router.
    // `replace`, not `push`: the web URL we are leaving is not reachable under the account we
    // just switched into, so keeping it in history would let Back bounce off the guard.
    // Keep navigation outside the pending window: a delayed route guard must not leave the
    // account trigger visibly disabled after the account data switch has already completed.
    await router.replace(landing)
  }
}
</script>

<template>
  <Skeleton v-if="state === 'loading'" class="account-switcher__skeleton" />
  <Select
    v-else-if="state === 'ready'"
    class="account-switcher"
    :model-value="accountId ?? null"
    :options="options"
    :label="props.compact ? '' : t('account.switcher.label')"
    :placeholder="t('account.switcher.placeholder')"
    :size="props.compact ? 'sm' : 'md'"
    :disabled="props.disabled || switching"
    :empty-label="t('account.switcher.empty')"
    :variant="props.compact ? 'picker' : 'default'"
    placement="bottom-start"
    :offset="props.compact ? 34 : 4"
    v-bind="props.compact ? { panelWidth: '16.5rem' } : {}"
    searchable
    @change="onChange"
  >
    <template v-if="props.compact" #trigger="{ open }">
      <span
        class="account-switcher__trigger flex w-full min-w-0 items-center justify-between gap-2xs text-[1rem]"
      >
        <strong>{{ account?.name }}</strong>
        <Icon :name="open ? 'chevron-up' : 'chevron-down'" :size="15" />
      </span>
    </template>
    <template v-if="props.compact" #option="{ option, selected }">
      <span class="account-switcher__option flex w-full min-w-0 items-center gap-xs text-fg">
        <span
          class="account-switcher__radio w-4 h-4 flex-none border border-border-strong rounded-full"
          :class="{ 'account-switcher__radio--selected': selected }"
          aria-hidden="true"
        />
        <span class="account-switcher__option-copy min-w-0 flex-1 text-start">
          <strong class="block overflow-hidden text-ellipsis text-nowrap">
            {{ optionName(option) }}
          </strong>
          <small class="block overflow-hidden text-ellipsis text-nowrap">
            {{ t('account.switcher.workspaceCount', optionWorkspaceCount(option)) }}
          </small>
        </span>
        <!-- Real text, not a colour cue: it is announced with the row, and it is what tells the
             user BEFORE they commit that this account opens the MCP surface and not the web one. -->
        <Badge
          v-if="optionIsMcpOnly(option)"
          tone="info"
          size="sm"
          class="account-switcher__mcp-badge flex-none"
          data-testid="account-switcher-mcp-badge"
        >
          {{ t('account.switcher.mcpOnly') }}
        </Badge>
      </span>
    </template>
  </Select>
  <Modal
    v-model="mcpConfirmOpen"
    :title="t('account.switcher.mcpConfirm.title', { name: mcpConfirmAccount?.name ?? '' })"
  >
    <template #body>
      <p class="m-0 text-sm text-muted" data-testid="account-switcher-mcp-confirm">
        {{ t('account.switcher.mcpConfirm.body', { host: mcpShellHost }) }}
      </p>
    </template>
    <template #footer>
      <div class="flex justify-end gap-xs">
        <Button variant="ghost" @click="mcpConfirmOpen = false">
          {{ t('account.switcher.mcpConfirm.cancel') }}
        </Button>
        <Button variant="primary" data-testid="account-switcher-mcp-go" @click="goToMcpShell">
          {{ t('account.switcher.mcpConfirm.go') }}
        </Button>
      </div>
    </template>
  </Modal>
</template>

<style scoped>
.account-switcher__skeleton .account-switcher__trigger strong,
.account-switcher__skeleton .account-switcher__option-copy > *,
.account-switcher .account-switcher__trigger strong,
.account-switcher .account-switcher__option-copy > * {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.account-switcher__skeleton .account-switcher__trigger strong {
  min-width: 0;
}
.account-switcher__skeleton .account-switcher__option-copy strong {
  font-size: 0.875rem;
  line-height: 1.2;
}
.account-switcher__skeleton .account-switcher__option-copy small {
  margin-top: 0.125rem;
  color: var(--ui-color-muted);
  font-size: 0.75rem;
  font-weight: 400;
}
.account-switcher__skeleton .account-switcher__radio--selected {
  border: 4px solid var(--ui-color-info);
}
.account-switcher .account-switcher__trigger strong {
  min-width: 0;
}
.account-switcher .account-switcher__option-copy strong {
  font-size: 0.875rem;
  line-height: 1.2;
}
.account-switcher .account-switcher__option-copy small {
  margin-top: 0.125rem;
  color: var(--ui-color-muted);
  font-size: 0.75rem;
  font-weight: 400;
}
.account-switcher .account-switcher__radio--selected {
  border: 4px solid var(--ui-color-info);
}
</style>
